import Talk from 'talkjs'
class InstantMessagingChat {
  static getOrInitInstance () {
    if (!InstantMessagingChat.instance) {
      InstantMessagingChat.instance = new InstantMessagingChat()
    }
    return InstantMessagingChat.instance
  }

  constructor () {
    this.ready = false
    this.talkSession = null
    this.talkVisitorUser = null
    this.chatbox = null
    this.currentCaseConversation = null
    this.htmlPanel = null
    this.onMessageEventCallback = () => {}
    this.unreadEventCallback = () => {}
  }

  async init (
    user,
    owner,
    { buildingId = '', buildingName = '' },
    { unreadEventCallback = () => {}, onMessageEventCallback = () => {} } = {},
    element
  ) {
    if (this.ready) {
      this.syncCurrentCaseConversation({ owner, buildingId, buildingName, element })
      return
    }
    await Talk.ready
    this.ready = true

    this.setupTalkVisitorUser(user)

    this.onMessageEventCallback = onMessageEventCallback
    this.unreadEventCallback = unreadEventCallback

    this.setTalkSession()

    this.setCurrentCaseConversation(owner, { buildingId, buildingName })

    this.mountChatbox(element)
  }

  setupTalkVisitorUser = user => {
    if (!this.ready) throw new Error('Must call init() first.')
    const visitorId = user.objectId || user.email
    this.talkVisitorUser = new Talk.User({
      id: visitorId,
      name: user.name || 'Visitor',
      email: user.email || null,
      photoUrl: user.profileUrl || null,
      role: 'default'
    })
  }

  setTalkSession = () => {
    if (!this.ready) throw new Error('Must call init() first.')
    this.talkSession = new Talk.Session({
      appId: process.env.IM_TALKJS_APP_ID,
      me: this.talkVisitorUser
    })

    this.talkSession.on('message', this.onMessageEventCallback)
    this.talkSession.unreads.on('change', this.unreadEventCallback)
  }

  setCurrentCaseConversation = async (
    owner,
    { buildingId = '', buildingName = '' }
  ) => {
    if (!this.ready) throw new Error('Must call init() first.')
    const owenerCompany = owner.companyInfo ? owner.companyInfo.company : owner.companyDisplayName
    const ownerName = owner.name + (owenerCompany ? ' | ' + owner.companyDisplayName : '') || ''

    const talkOwner = new Talk.User({
      id: owner.objectId,
      name: ownerName || 'Owner',
      email: owner.email || null,
      photoUrl: owner.companyInfo ? owner.companyInfo.logoUrl || null : owner.profileUrl || null,
      role: 'default'
    })

    const caseId = `b_${buildingId}`

    this.currentCaseConversation = this.talkSession.getOrCreateConversation(`${owner.objectId}__${this.talkVisitorUser.id}__${caseId}`)
    this.currentCaseConversation.setParticipant(this.talkVisitorUser)
    this.currentCaseConversation.setParticipant(talkOwner)
    this.currentCaseConversation.setAttributes({
      subject: buildingName || null,
      custom: {
        caseId,
        caseName: buildingName,
        ownerName,
        ownerId: owner.objectId,
        visitorName: this.talkVisitorUser.name,
        visitorId: this.talkVisitorUser.id
      }
    })
  }

  mountChatbox = el => {
    if (!this.ready) throw new Error('Must call init() first.')
    this.chatbox = this.talkSession.createChatbox(this.currentCaseConversation, { theme: '2d-viewer' })
    this.chatbox.mount(el)
  }

  createHtmlPanel = async options => {
    if (!this.ready) throw new Error('Must call init() first.')
    this.htmlPanel = await this.chatbox.createHtmlPanel(options)
  }

  destroySession = () => {
    if (!this.ready) throw new Error('Must call init() first.')
    if (this.talkSession) {
      this.talkSession.destroy()
    }
    this.talkSession = null
  }

  destroyHtmlPanel = async () => {
    if (!this.ready) throw new Error('Must call init() first.')
    if (this.htmlPanel) {
      await this.htmlPanel.destroy()
    }
    this.htmlPanel = null
  }

  subscribeUnreadEvents = callback => {
    if (!this.ready) throw new Error('Must call init() first.')
    this.talkSession.unreads.on('change', callback)
  }

  getCurrentCaseConversation = () => {
    if (!this.ready) throw new Error('Must call init() first.')
    return this.currentCaseConversation || {}
  }

  switchConversation = conversation => {
    if (!this.ready) throw new Error('Must call init() first.')
    this.chatbox.select(conversation)
  }

  getConversationById = id => {
    if (!this.ready) throw new Error('Must call init() first.')
    return this.talkSession.getOrCreateConversation(id)
  }

  toggleHtmlPanel = bool => {
    if (!this.ready) throw new Error('Must call init() first.')
    if (!this.htmlPanel) return
    try {
      if (bool) {
        this.htmlPanel.show()
      } else {
        this.htmlPanel.hide()
      }
    } catch (error) {
      console.log(error)
    }
  }

  async syncCurrentCaseConversation ({ owner, buildingId, buildingName, element }) {
    this.destroySession()
    this.setTalkSession()
    await this.setCurrentCaseConversation(owner, { buildingId, buildingName })
    await this.mountChatbox(element)
  }
}

export default InstantMessagingChat
